import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, AlertTriangle, Package, Edit, Trash2, Upload, X, Ban, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth, API, getImageUrl } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import { compressImage } from '../../utils/imageCompressor';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { formatAmount } from '../../utils/format';
const IngredientsTab = () => {
  const { getAuthHeaders } = useAuth();
  const [ingredients, setIngredients] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    unit: 'kg',
    cost_per_unit: 0,
    current_stock: 0,
    min_stock: 0,
    image_url: ''
  });

  useEffect(() => {
    loadIngredients();
  }, []);

  const loadIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients`, {
        headers: getAuthHeaders()
      });
      setIngredients(Array.isArray(response.data) ? response.data : []);
    } catch {
      toast.error('Error al cargar ingredientes');
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Comprimir en el navegador (redimension a 800x800 + WebP): se acepta
    // cualquier imagen sin tope de tamano porque llega ya optimizada al backend.
    const compressedFile = await compressImage(file);
    setImageFile(compressedFile);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(compressedFile);
  };

  const handleCreateIngredient = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let finalImageUrl = formData.image_url;

      // Upload image if file selected
      if (imageFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', imageFile);

        const uploadRes = await axios.post(
          `${API}/ingredients/upload-image`,
          formDataUpload,
          {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        finalImageUrl = uploadRes.data.image_url;
      }

      const ingredientData = {
        ...formData,
        image_url: finalImageUrl
      };

      if (editingIngredient) {
        // Update existing ingredient
        await axios.put(`${API}/ingredients/${editingIngredient.id}`, ingredientData, {
          headers: getAuthHeaders()
        });
        toast.success('Ingrediente actualizado exitosamente');
      } else {
        // Create new ingredient
        await axios.post(`${API}/ingredients`, ingredientData, {
          headers: getAuthHeaders()
        });
        toast.success('Ingrediente creado exitosamente');
      }
      
      await loadIngredients();
      handleCloseDialog();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar ingrediente'));
    }

    setLoading(false);
  };

  const handleEditIngredient = (ingredient) => {
    setEditingIngredient(ingredient);
    setFormData({
      name: ingredient.name,
      unit: ingredient.unit,
      cost_per_unit: ingredient.cost_per_unit,
      current_stock: ingredient.current_stock,
      min_stock: ingredient.min_stock,
      image_url: ingredient.image_url || ''
    });
    setImagePreview(ingredient.image_url || null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      setEditingIngredient(null);
      setFormData({ name: '', unit: 'kg', cost_per_unit: 0, current_stock: 0, min_stock: 0, image_url: '' });
      setImageFile(null);
      setImagePreview(null);
    }, 100);
  };

  const handleOpenDialog = () => {
    setEditingIngredient(null);
    setFormData({ name: '', unit: 'kg', cost_per_unit: 0, current_stock: 0, min_stock: 0, image_url: '' });
    setImageFile(null);
    setImagePreview(null);
    setIsDialogOpen(true);
  };

  const handleDeleteIngredient = async (ingredientId) => {
    if (!window.confirm('¿Estás seguro de eliminar este ingrediente?')) {
      return;
    }

    try {
      await axios.delete(`${API}/ingredients/${ingredientId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Ingrediente eliminado exitosamente');
      loadIngredients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar ingrediente'));
    }
  };

  const handleToggleActive = async (ingredient) => {
    try {
      await axios.patch(
        `${API}/ingredients/${ingredient.id}/toggle-active`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Ingrediente ${ingredient.is_active !== false ? 'deshabilitado' : 'habilitado'}`);
      loadIngredients();
    } catch {
      toast.error('Error al cambiar estado del ingrediente');
    }
  };

  const lowStockIngredients = ingredients.filter(ing => ing.current_stock <= ing.min_stock);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Inventario de Ingredientes</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={handleOpenDialog}
              data-testid="create-ingredient-button" 
              className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Ingrediente
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingIngredient ? 'Editar Ingrediente' : 'Crear Nuevo Ingrediente'}</DialogTitle>
              <DialogDescription>
                {editingIngredient
                  ? 'Modifica los datos del ingrediente existente'
                  : 'Agrega un nuevo ingrediente al inventario'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateIngredient} className="space-y-4">
              <div>
                <Label>Nombre</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Imagen del Ingrediente</Label>
                <div className="space-y-3">
                  {/* File upload input */}
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="rounded-xl"
                      id="ingredient-image-upload"
                    />
                    <Label htmlFor="ingredient-image-upload" className="cursor-pointer">
                      <div className="px-4 py-2 bg-orange-100 text-orange-700 rounded-xl hover:bg-orange-200 transition-colors flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Subir
                      </div>
                    </Label>
                  </div>

                  {/* Image preview */}
                  {(imagePreview || formData.image_url) && (
                    <div className="relative w-full h-48 bg-gray-100 rounded-xl border-2 border-orange-200 overflow-hidden">
                      <img 
                        src={imagePreview || getImageUrl(formData.image_url)} 
                        alt="Preview" 
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x300?text=Error+al+cargar+imagen';
                        }}
                      />
                      {imagePreview && (
                        <div className="absolute top-2 right-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setImageFile(null);
                              setImagePreview(null);
                            }}
                            className="rounded-full w-8 h-8 p-0"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Formatos soportados: JPG, PNG, GIF. Tamaño máximo: 5MB
                  </p>
                </div>
              </div>
              <div>
                <Label>Unidad de Medida</Label>
                <Input
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder="kg, L, unidades, etc."
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Costo por Unidad ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cost_per_unit}
                  onChange={(e) => setFormData({ ...formData, cost_per_unit: parseFloat(e.target.value) })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Stock Actual</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.current_stock}
                  onChange={(e) => setFormData({ ...formData, current_stock: parseFloat(e.target.value) })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Stock Mínimo (Alerta)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.min_stock}
                  onChange={(e) => setFormData({ ...formData, min_stock: parseFloat(e.target.value) })}
                  required
                  className="rounded-xl"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                {loading ? 'Guardando...' : (editingIngredient ? 'Actualizar Ingrediente' : 'Crear Ingrediente')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {lowStockIngredients.length > 0 && (
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">
              {lowStockIngredients.length} ingrediente(s) con stock bajo
            </span>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {ingredients.map((ingredient) => {
          const isLowStock = ingredient.current_stock <= ingredient.min_stock;
          return (
            <Card
              key={ingredient.id}
              data-testid={`ingredient-card-${ingredient.id}`}
              className={`overflow-hidden bg-white/80 backdrop-blur-xl border-orange-100 hover-lift ${
                isLowStock ? 'border-red-300 border-2' : ''
              } ${ingredient.is_active === false ? 'opacity-60 border-gray-300' : ''}`}
            >
              {ingredient.image_url && (
                <div className="h-48 w-full overflow-hidden bg-gradient-to-br from-orange-100 to-red-100">
                  <img 
                    src={getImageUrl(ingredient.image_url)} 
                    alt={ingredient.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-16 h-16 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg></div>';
                    }}
                  />
                </div>
              )}
              {!ingredient.image_url && (
                <div className="h-48 w-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                  <Package className="w-16 h-16 text-white" />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">{ingredient.name}</h3>
                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${
                      ingredient.is_active !== false
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {ingredient.is_active !== false ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  {isLowStock && (
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Stock Bajo
                    </span>
                  )}
                </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Stock Actual:</span>
                  <span className={`font-semibold ${isLowStock ? 'text-red-600' : 'text-green-600'}`}>
                    {ingredient.current_stock} {ingredient.unit}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Stock Mínimo:</span>
                  <span className="font-semibold text-gray-800">
                    {ingredient.min_stock} {ingredient.unit}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Costo Unitario:</span>
                  <span className="font-semibold text-orange-600">
                    ${formatAmount(ingredient.cost_per_unit)}
                  </span>
                </div>
              </div>
                <div className="flex gap-2">
                <Button
                  onClick={() => handleToggleActive(ingredient)}
                  variant="outline"
                  size="sm"
                  className={`flex-1 rounded-xl ${
                    ingredient.is_active !== false
                      ? 'border-yellow-300 text-yellow-700 hover:bg-yellow-50'
                      : 'border-green-300 text-green-700 hover:bg-green-50'
                  }`}
                >
                  {ingredient.is_active !== false ? (
                    <><Ban className="w-4 h-4 mr-2" /> Deshabilitar</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" /> Habilitar</>
                  )}
                </Button>
                <Button
                  onClick={() => handleEditIngredient(ingredient)}
                  variant="outline"
                  size="sm"
                  data-testid={`edit-ingredient-${ingredient.id}`}
                  className="flex-1 border-orange-200 hover:bg-orange-50 rounded-xl"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
                <Button
                  onClick={() => handleDeleteIngredient(ingredient.id)}
                  variant="outline"
                  size="sm"
                  data-testid={`delete-ingredient-${ingredient.id}`}
                  className="flex-1 border-red-200 hover:bg-red-50 text-red-600 rounded-xl"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {ingredients.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No hay ingredientes registrados</p>
          <Button onClick={() => setIsDialogOpen(true)} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
            <Plus className="w-4 h-4 mr-2" />
            Crear Primer Ingrediente
          </Button>
        </div>
      )}
    </div>
  );
};

export default IngredientsTab;
